<template>
  <div class="offer-fields-display">
    <!-- Taxonomy chips -->
    <div v-if="attrs.tags && !attrs.minimal" class="offer-fields-display__chips mb-3">
      <article v-if="hasItems(item.categories)">
        <p class="text-caption text-uppercase mb-1">{{ taxonomyLabel('categories', 'Categories') }}</p>
        <div class="d-flex flex-wrap">
          <v-chip v-for="(tag, i) in item.categories" :key="`${idPrefix}-cat-${i}`" x-small light
            class="py-2 px-2 mr-1 mb-1 pink lighten-3">
            {{ resolveTitle('categories', tag) }}
          </v-chip>
        </div>
      </article>

      <article v-if="hasItems(item.assets)" class="mt-2">
        <p class="text-caption text-uppercase mb-1">{{ taxonomyLabel('assets', 'Assets / Brands') }}</p>
        <div class="d-flex flex-wrap">
          <v-chip v-for="(tag, i) in item.assets" :key="`${idPrefix}-asset-${i}`" x-small light
            class="py-2 px-2 mr-1 mb-1 green lighten-4">
            {{ resolveTitle('assets', tag) }}
          </v-chip>
        </div>
      </article>

      <article v-if="hasItems(item.tags)" class="mt-2">
        <p class="text-caption text-uppercase mb-1">{{ taxonomyLabel('tags', 'Tags') }}</p>
        <div class="d-flex flex-wrap">
          <v-chip v-for="(tag, i) in item.tags" :key="`${idPrefix}-tag-${i}`" x-small light
            class="py-2 px-2 mr-1 mb-1 yellow lighten-3">
            {{ resolveTitle('tags', tag) }}
          </v-chip>
        </div>
      </article>

      <article v-if="hasItems(item.audiences)" class="mt-2">
        <p class="text-caption text-uppercase mb-1">{{ taxonomyLabel('audiences', 'Audiences') }}</p>
        <div class="d-flex flex-wrap">
          <v-chip v-for="(tag, i) in item.audiences" :key="`${idPrefix}-aud-${i}`" x-small light
            class="py-2 px-2 mr-1 mb-1 orange lighten-3">
            {{ resolveTitle('audiences', tag) }}
          </v-chip>
        </div>
      </article>

      <article v-if="hasItems(item.features)" class="mt-2">
        <p class="text-caption text-uppercase mb-1">{{ taxonomyLabel('features', 'Features') }}</p>
        <div class="d-flex flex-wrap">
          <ul>
            <li v-for="(tag, i) in item.features" :key="`${idPrefix}-feat-${i}`" x-small light class="">
              {{ tag }}
            </li>
          </ul>
        </div>
      </article>

      <article v-if="hasItems(item.benefits)" class="mt-2">
        <p class="text-caption text-uppercase mb-1">{{ taxonomyLabel('benefits', 'Benefits') }}</p>
        <div class="d-flex flex-wrap">
          <ul>
            <li v-for="(tag, i) in item.benefits" :key="`${idPrefix}-ben-${i}`" x-small light>
              {{ tag }}
            </li>
          </ul>
        </div>
      </article>
    </div>

    <!-- Contribution sliders (read-only) -->
    <div v-if="attrs.contribution && !attrs.minimal && hasContributionValues"
      class="offer-fields-display__contribution mb-3 mt-3">
      <p class="text-caption text-uppercase mb-2">Brand contribution</p>
      <div class="d-flex">
        <div class="flex-1">
          <p class="ma-0 pa-0 text-caption">Brand stretch ({{ item.stretch || 0 }}/10)</p>
          <v-slider :value="item.stretch || 0" :min="0" :max="10" step="1" ticks="never" tick-size="4" color="amber"
            track-color="grey lighten-2" class="ma-0 pa-0" hide-details readonly />
        </div>
        <div class="flex-1">
          <p class="ma-0 pa-0 text-caption">Contribution ({{ item.contribution || 0 }}/10)</p>
          <v-slider :value="item.contribution || 0" :min="0" :max="10" step="1" ticks="never" tick-size="4"
            color="amber" track-color="grey lighten-2" class="ma-0 pa-0" hide-details readonly />
        </div>
        <div class="flex-1">
          <p class="ma-0 pa-0 text-caption">Reputation ({{ item.reputation || 0 }}/10)</p>
          <v-slider :value="item.reputation || 0" :min="0" :max="10" step="1" ticks="never" tick-size="4" color="amber"
            track-color="grey lighten-2" class="ma-0 pa-0" hide-details readonly />
        </div>
      </div>
    </div>

    <!-- Radar -->
    <div v-if="attrs.graph && !attrs.minimal && hasNonZeroScores(item.scores)" class="offer-fields-display__radar mb-3">
      <BarRadar :data="item.scores" :id="`${idPrefix}-radar`" />
    </div>

    <!-- Stats sliders (read-only) -->
    <div v-if="attrs.stats && !attrs.minimal && item.scores" class="offer-fields-display__stats mb-3">
      <p class="text-caption text-uppercase mb-1">Scores</p>
      <div v-for="score in scoreFields" :key="`${idPrefix}-score-${score.key}`">
        <p class="ma-0 pa-0 text-caption">{{ score.label }}</p>
        <v-slider :value="(item.scores && item.scores[score.key]) || 0" :min="0" :max="5" step="1" ticks="always"
          tick-size="4" color="amber" track-color="grey lighten-2" class="ma-0 pa-0" hide-details readonly />
      </div>
    </div>
  </div>
</template>

<script>
import { BarRadar } from '@/components/graphs'
import { scoreFields } from './model'

export default {
  name: 'OfferFieldsDisplay',

  components: {
    BarRadar,
  },

  props: {
    // Data being displayed (group or card)
    item: {
      type: Object,
      default: () => ({}),
    },

    // Display toggles + local taxonomy lookup
    attrs: {
      type: Object,
      default: () => ({}),
    },

    // Prefix used for unique Vue list keys / element ids
    idPrefix: {
      type: String,
      default: 'of',
    },
  },

  data() {
    return {
      scoreFields,
    }
  },

  computed: {
    hasContributionValues() {
      return (
        (this.item?.stretch || 0) > 0 ||
        (this.item?.contribution || 0) > 0 ||
        (this.item?.reputation || 0) > 0
      )
    },
  },

  methods: {
    hasItems(arr) {
      return Array.isArray(arr) && arr.length > 0
    },

    taxonomyLabel(key, fallback) {
      const custom = this.attrs?.local?.labels?.[key]
      if (!custom || !custom.trim()) return fallback
      const trimmed = custom.trim()
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    },

    resolveTitle(type, id) {
      const items = this.attrs?.local?.[type]
      if (!Array.isArray(items)) return id
      const found = items.find((it) => it.id === id)
      return found?.title || id
    },

    hasNonZeroScores(scores) {
      if (!scores || typeof scores !== 'object') return false
      return Object.values(scores).some((val) => val > 0)
    },
  },
}
</script>
